import { Cancel } from "@mui/icons-material";
import { Card, CircularProgress, Grid, MenuItem, TextField } from "@mui/material";
import ImagePicker from "components/ApnaUploader";
import AstrieskIcon from "components/AstrieskIcon";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { handleAlert } from "redux/festures/alertSlice";
import {
  creatCategoryData,
  getNullParentCategory,
  updateCategory,
} from "redux/festures/categorySlice";

const initialForm = {
  name: "",
  description: "",
  workExperience: "",
  categoryStatus: "approved",
  disable: "false",
  adminCharge: 0,
};

const allowedImageTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

const getPreviewUrl = (path) => {
  if (!path) return "";
  return `${process.env.REACT_APP_URI}${path}`;
};

export default function PcatogeroyUpdate({ viewData, isPUpdates, setIsOpenUpdate, onSuccess }) {
  const dispatch = useDispatch();
  const adminId = localStorage.getItem("admin_id");
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const { createUpdateLoading } = useSelector((state) => state.isCategory);

  const [formData, setFormData] = useState(initialForm);
  const [iconFile, setIconFile] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  const existingBanner = useMemo(
    () => viewData?.banner?.find((item) => item?.type === "IMAGE") || null,
    [viewData]
  );

  useEffect(() => {
    if (viewData && isPUpdates) {
      setFormData({
        name: viewData?.name || "",
        description: viewData?.description || "",
        adminCharge: viewData?.adminCharge || 0,
        workExperience:
          viewData?.workExperience !== undefined && viewData?.workExperience !== null
            ? String(viewData.workExperience)
            : "",
        categoryStatus: viewData?.categoryStatus || "approved",
        disable: String(Boolean(viewData?.disable)),
      });
    } else {
      setFormData(initialForm);
    }

    setIconFile(null);
    setBannerFile(null);
    setFormErrors({});
  }, [viewData, isPUpdates]);


  const validateField = (name, value) => {
    switch (name) {
      case "name":
        if (!value?.trim()) return "Parent service name is required";
        if (!/^[A-Za-z ]+$/.test(value.trim())) return "Only alphabets and spaces are allowed";
        if (value.trim().length < 2) return "Name should be at least 2 characters";
        return "";
      case "description":
        if (!value?.trim()) return "Description is required";
        if (value.trim().length < 3) return "Description should be at least 3 characters";
        return "";
      case "workExperience":
        if (value === "" || value === null) return "Work experience is required";
        if (Number(value) < 0) return "Work experience cannot be negative";
        return "";
      // case "categoryStatus":
      //   if (!value) return "Category status is required";
      //   return "";
      case "adminCharge":
        if (!value) return "Admin charge is required";
        if (Number(value) < 0) return "Admin charge cannot be negative";
        return "";
      case "disable":
        if (!["true", "false"].includes(String(value))) return "Disable value is invalid";
        return "";
      default:
        return "";
    }
  };

  const validateFiles = () => {
    const errors = {};

    if (!iconFile && !viewData?.icon) {
      errors.icon = "Icon image is required";
    } else if (iconFile && !allowedImageTypes.includes(iconFile.type)) {
      errors.icon = "Icon must be jpg, jpeg, png, or webp";
    }

    if (!bannerFile && !existingBanner?.url) {
      errors.banner = "Banner image is required";
    } else if (bannerFile && !allowedImageTypes.includes(bannerFile.type)) {
      errors.banner = "Banner must be jpg, jpeg, png, or webp";
    }

    return errors;
  };

  const validateForm = () => {
    const nextErrors = {};

    Object.entries(formData).forEach(([key, value]) => {
      const error = validateField(key, value);
      if (error) nextErrors[key] = error;
    });

    const fileErrors = validateFiles();
    const allErrors = { ...nextErrors, ...fileErrors };
    setFormErrors(allErrors);

    return Object.keys(allErrors).length === 0;
  };

  const refreshCategories = () => {
    if (adminId) dispatch(getNullParentCategory(adminId));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "name" && !/^[A-Za-z ]*$/.test(value)) return;
    if (name === "workExperience" && value !== "" && !/^\d*\.?\d*$/.test(value)) return;
    if (name === "adminCharge" && value !== "" && !/^\d{0,3}(\.\d{0,2})?$/.test(value)) return;

    setFormData((prev) => ({ ...prev, [name]: value }));

    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: validateField(name, value),
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!adminId) {
      dispatch(
        handleAlert({
          isOpen: true,
          type: "error",
          msg: "Admin id is missing. Please login again.",
        })
      );
      return;
    }

    if (!validateForm()) {
      dispatch(
        handleAlert({
          isOpen: true,
          type: "warning",
          msg: "Please fix all validation errors before submit",
        })
      );
      return;
    }

    const payload = new FormData();
    payload.append("name", formData.name.trim());
    payload.append("description", formData.description.trim());
    payload.append("workExperience", formData.workExperience);
    // payload.append("status", formData.status);
    payload.append("disable", formData.disable);
    payload.append("adminCharge", formData.adminCharge);

    if (iconFile) payload.append("icon", iconFile);
    if (bannerFile) payload.append("banner", bannerFile);

    const response = await dispatch(
      isPUpdates
        ? updateCategory({
            url: `/updateCategory/${viewData?._id}/${adminId}`,
            data: payload,
          })
        : creatCategoryData({
            url: `/createCategory/${adminId}`,
            reqBody: payload,
          })
    );

    dispatch(
      handleAlert({
        isOpen: true,
        type: response?.payload?.success ? "success" : "error",
        msg:
          response?.payload?.message ||
          (isPUpdates
            ? "Parent service updated successfully"
            : "Parent service created successfully"),
      })
    );

    if (response?.payload?.success) {
      refreshCategories();
      setIsOpenUpdate(false);
    }
  };

  return (
    <MDBox
      display="flex"
      alignItems="center"
      lineHeight={1}
      sx={({ palette: { dark, white, info } }) => ({
        border: 0.5,
        borderColor: darkMode ? white.main : dark.main,
        borderRadius: 3,
        p: 2,
        width: "100%",
        //   height: "70vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        gap: 2,
      })}
    >
      {/* <MDBox
          mx={2}
          mt={-3}
          py={3}
          px={2}
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <MDTypography variant="h6" color="white">
            {isPUpdates ? "Update Parent Service" : "Create Parent Service"}
          </MDTypography>
          <MDTypography variant="button" color="white" fontWeight="regular">
            Service Category Form
          </MDTypography>
        </MDBox> */}
      <Card
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 , 10px",
        }}
      >
        <MDTypography variant="h6" py={0.9}>
          {isPUpdates ? "Update Parent Service" : "Create Parent Service"}
        </MDTypography>
      </Card>

      <MDBox component="form" onSubmit={handleSubmit} p={3}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <MDTypography variant="h6">
              Parent Service Name <AstrieskIcon />
            </MDTypography>
            <MDInput
              fullWidth
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter parent service name"
              error={Boolean(formErrors.name)}
              sx={{ mt: 1 }}
              disabled={createUpdateLoading}
            />
            {formErrors.name ? (
              <MDTypography variant="caption" color="error">
                {formErrors.name}
              </MDTypography>
            ) : null}
          </Grid>

          <Grid item xs={12} md={6}>
            <MDTypography variant="h6">
              Work Experience (Years) <AstrieskIcon />
            </MDTypography>
            <MDInput
              fullWidth
              name="workExperience"
              value={formData.workExperience}
              onChange={handleInputChange}
              placeholder="Enter work experience"
              type="text"
              error={Boolean(formErrors.workExperience)}
              sx={{ mt: 1 }}
              disabled={createUpdateLoading}
            />
            {formErrors.workExperience ? (
              <MDTypography variant="caption" color="error">
                {formErrors.workExperience}
              </MDTypography>
            ) : null}
          </Grid>

          <Grid item xs={12}>
            <MDTypography variant="h6">
              Description <AstrieskIcon />
            </MDTypography>
            <MDInput
              fullWidth
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Write parent service description"
              multiline
              rows={4}
              error={Boolean(formErrors.description)}
              sx={{ mt: 1 }}
              disabled={createUpdateLoading}
            />
            {formErrors.description ? (
              <MDTypography variant="caption" color="error">
                {formErrors.description}
              </MDTypography>
            ) : null}
          </Grid>

          {/* <Grid item xs={12} md={6}>
            <MDTypography variant="h6">
              Status <AstrieskIcon />
            </MDTypography>
            <TextField
              select
              fullWidth
              size="small"
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              error={Boolean(formErrors.status)}
              helperText={formErrors.status}
              disabled={createUpdateLoading}
              sx={{ mt: 1,height: "40px" }}
            >
              <MenuItem value="active">active</MenuItem>
              <MenuItem value="inactive">inactive</MenuItem>
            </TextField>
          </Grid> */}

          {/* <Grid item xs={12} md={6}>
            <MDTypography variant="h6">
              Disable <AstrieskIcon />
            </MDTypography>
            <TextField
              select
              fullWidth
              size="small"
              name="disable"
              value={formData.disable}
              onChange={handleInputChange}
              error={Boolean(formErrors.disable)}
              helperText={formErrors.disable}
              disabled={createUpdateLoading}
              sx={{ mt: 1,height: "40px" }}
            >
              <MenuItem value="false">false</MenuItem>
              <MenuItem value="true">true</MenuItem>
            </TextField>
          </Grid> */}
          <Grid item xs={12}>
            <MDBox
              display="flex"
              alignItems="flex-start"
              flexDirection="column"
              lineHeight={1}
              gap={1.5}
              width={"100%"}
            >
              <MDTypography variant="h6">
                Admin Commission (%) <AstrieskIcon />
              </MDTypography>
              <MDInput
                disabled={createUpdateLoading}
                required={true}
                name="adminCharge"
                placeholder="Enter admin commission"
                type="number"
                fullWidth
                value={formData.adminCharge}
                onChange={handleInputChange}
                inputProps={{ min: 0, max: 100, step: "0.01" }}
              />
              {formErrors?.adminCharge ? (
                <MDTypography variant="caption" color="error">
                  {formErrors.adminCharge}
                </MDTypography>
              ) : (
                <MDTypography variant="caption" color="text">
                  Maximum allowed value is 100.
                </MDTypography>
              )}
            </MDBox>
          </Grid>

          <Grid item xs={12} md={6}>
            <MDTypography variant="h6">
              Icon Image <AstrieskIcon />
              <MDTypography variant="body1" component="span" fontSize={11}>
                &nbsp; ( image size - 178 × 178 px )
              </MDTypography>
            </MDTypography>
            <MDBox mt={1}>
              <ImagePicker
                name="parent-service-icon"
                images={iconFile}
                setImages={setIconFile}
                multiple={false}
                accept="image/*"
                disabled={createUpdateLoading}
              />
            </MDBox>
            {!iconFile && viewData?.icon ? (
              <MDBox mt={1} display="flex" alignItems="center" gap={1}>
                <img
                  src={getPreviewUrl(viewData.icon)}
                  alt="existing service icon"
                  style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 8 }}
                />
                <Cancel sx={{ color: "text.secondary" }} />
              </MDBox>
            ) : null}
            {formErrors.icon ? (
              <MDTypography variant="caption" color="error">
                {formErrors.icon}
              </MDTypography>
            ) : null}
          </Grid>

          <Grid item xs={12} md={6}>
            <MDTypography variant="h6">
              Banner Image <AstrieskIcon />
              <MDTypography variant="body1" component="span" fontSize={11}>
                &nbsp; ( image size - 178 × 178 px )
              </MDTypography>
            </MDTypography>
            <MDBox mt={1}>
              <ImagePicker
                name="parent-service-banner"
                images={bannerFile}
                setImages={setBannerFile}
                multiple={false}
                accept="image/*"
                disabled={createUpdateLoading}
              />
            </MDBox>
            {!bannerFile && existingBanner?.url ? (
              <MDBox mt={1} display="flex" alignItems="center" gap={1}>
                <img
                  src={getPreviewUrl(existingBanner.url)}
                  alt="existing banner"
                  style={{ width: 96, height: 72, objectFit: "cover", borderRadius: 8 }}
                />
                <Cancel sx={{ color: "text.secondary" }} />
              </MDBox>
            ) : null}
            {formErrors.banner ? (
              <MDTypography variant="caption" color="error">
                {formErrors.banner}
              </MDTypography>
            ) : null}
          </Grid>
        </Grid>

        <MDBox
          mt={3}
          sx={{
            display: "flex",
            justifyContent: { xs: "stretch", md: "flex-end" },
            gap: 1.5,
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          <MDButton
            variant="outlined"
            color="secondary"
            disabled={createUpdateLoading}
            onClick={() => setIsOpenUpdate(false)}
          >
            Cancel
          </MDButton>
          <MDButton type="submit" color="info" variant="gradient" disabled={createUpdateLoading}>
            {createUpdateLoading ? (
              <CircularProgress color="inherit" size={20} />
            ) : isPUpdates ? (
              "Update Parent Service"
            ) : (
              "Create Parent Service"
            )}
          </MDButton>
        </MDBox>
      </MDBox>
    </MDBox>
  );
}

PcatogeroyUpdate.propTypes = {
  viewData: PropTypes.any,
  isPUpdates: PropTypes.bool,
  setIsOpenUpdate: PropTypes.func,
  onSuccess: PropTypes.func,
};
